// Hooks del módulo deals — espejo del patrón de expo-capture (sin react-query).
// Estado local + refetch manual; suficiente para v0.1.

import { useCallback, useEffect, useState } from 'react';

import { dealsApi, DealsApiError } from '../api';
import type {
  Deal,
  DealCreateIn,
  DealListFilters,
  DealStage,
  DealStageMoveIn,
  DealUpdateIn,
  ForecastReport,
} from '../types';

function describe(e: unknown): string {
  if (e instanceof DealsApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Error desconocido';
}

// ── Stages ───────────────────────────────────────────────────

export function useDealStages() {
  const [stages, setStages] = useState<DealStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dealsApi.listStages();
      setStages(res);
    } catch (e) {
      setError(describe(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { stages, loading, error, refetch };
}

// ── Deals list (con filtros) ─────────────────────────────────

export function useDeals(filters: DealListFilters = {}) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filterKey = JSON.stringify(filters);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dealsApi.list(filters);
      setDeals(res);
    } catch (e) {
      setError(describe(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { deals, setDeals, loading, error, refetch };
}

// ── Mutations ────────────────────────────────────────────────

export function useCreateDeal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (data: DealCreateIn) => {
    setLoading(true);
    setError(null);
    try {
      return await dealsApi.create(data);
    } catch (e) {
      setError(describe(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading, error };
}

export function useUpdateDeal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (id: string, data: DealUpdateIn) => {
    setLoading(true);
    setError(null);
    try {
      return await dealsApi.update(id, data);
    } catch (e) {
      setError(describe(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading, error };
}

export function useMoveDealStage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (id: string, data: DealStageMoveIn) => {
    setLoading(true);
    setError(null);
    try {
      return await dealsApi.moveStage(id, data);
    } catch (e) {
      setError(describe(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading, error };
}

// ── Forecast ─────────────────────────────────────────────────

export function useForecast() {
  const [report, setReport] = useState<ForecastReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dealsApi.getForecast();
      setReport(res);
    } catch (e) {
      setError(describe(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { report, loading, error, refetch };
}
